import type { Call } from '@entities/call';
import { CallTypes } from '@entities/call';
import type { RingIncomingCallRequest } from '@usecases/ring-incoming-call.usecase';
import type { CallRepository } from '@infra/repositories/call.repository';
import type { AcceptIncomingCallRequest } from '@usecases/accept-incoming-call.usecase';

export class MemCallRepository implements CallRepository {
  private calls: Call[];

  constructor(calls: Call[] = []) {
    this.calls = calls;
  }

  async insertCall(call: Call): Promise<void> {
    this.calls.push(call);
  }

  async removeCalls(): Promise<void> {
    this.calls = [];
  }

  async removeTransferCalls(): Promise<void> {
    this.calls = this.calls.filter(
      (call) => call.type !== CallTypes.ADD_PARTICIPANT,
    );
  }

  async updateIncomingCall(call: AcceptIncomingCallRequest): Promise<void> {
    const currentCall = <AcceptIncomingCallRequest>this.calls[0];

    currentCall.crmMetadata = {
      ...(currentCall.crmMetadata ?? { vendorCallId: '' }),
      ...call.crmMetadata,
    };
  }

  async fetchCallById(callId: string): Promise<Call | undefined> {
    return this.calls.find((call) => call.id === callId);
  }

  async updateCallById(id: string, call: Call): Promise<void> {
    const callIndex = this.calls.findIndex((cl) => cl.id === id);
    this.calls[callIndex] = call;
  }

  async updateHoldStatus(id: string, isOnHold: boolean): Promise<void> {
    const callIndex = this.calls.findIndex((cl) => cl.id === id);
    this.calls[callIndex].isOnHold = isOnHold;
  }

  async updateTransferHoldStatus(to: string, isOnHold: boolean): Promise<void> {
    const callIndex = this.calls.findIndex((cl) => cl.to === to);
    this.calls[callIndex].isOnHold = isOnHold;
  }

  async insertIncomingCall(
    incomingCall: RingIncomingCallRequest,
  ): Promise<void> {
    const call: Call = {
      ...incomingCall,
      isMuted: false,
    };

    return this.insertCall(call);
  }

  async getCurrentCall() {
    return this.calls[0];
  }

  async getCalls(): Promise<Call[]> {
    return [...this.calls];
  }

  getCallForHoldToggle(): Promise<Call<unknown>> {
    return this.getCurrentCall();
  }

  async insertOutboundCall(call: Call<unknown>): Promise<void> {
    return this.insertCall(call);
  }

  async fetchOutboundCall(): Promise<Call> {
    return this.getCurrentCall();
  }

  async fetchTransferCall(): Promise<Call> {
    return this.calls.filter(
      (call) => call.type === CallTypes.ADD_PARTICIPANT,
    )[0];
  }
}
